/**
 * Inbox conversation search.
 *
 * The conversation list is built from the *messages* index, which carries only
 * phone numbers (`from`/`to`) — no name field. So a name query cannot be
 * answered by that index at all. Resolution runs in two steps instead:
 *
 *   1. Match the query against contacts (name OR cellPhone), which is the
 *      index that actually knows names.
 *   2. Turn those contacts' phone numbers into `from`/`to` clauses for the
 *      message search, alongside a digit match for numbers that have no
 *      contact record (first-time texters).
 */

import { MIN_PHONE_DIGITS } from 'lib/contactSearch'
import { ES_SPECIAL_CHARS } from 'lib/esSpecialChars'
import { phoneDigits } from 'lib/phone'

/**
 * Escape a user-typed fragment for interpolation into a query_string clause.
 * Wildcards we add ourselves must be appended *after* this runs.
 */
export function escapeLucene(value: string): string {
  return value.replace(ES_SPECIAL_CHARS, (character) => `\\${character}`)
}

/**
 * Cap on resolved contacts. Deliberately generous: one person routinely has a
 * contact record per location, and they can carry different numbers (measured on
 * dev: one name, four records, three distinct phones). Deduped phones at 2
 * clauses each stay far under Lucene's 1024 max_clause_count.
 */
export const MAX_SEARCH_CONTACTS = 200

/**
 * Contacts search for an inbox query: name fragments AND-ed so "sarah smith"
 * needs both, plus the digit form so a typed number finds its contact.
 * Returns undefined for a query with nothing to match on.
 *
 * Deliberately NOT scoped to the current location. Contacts are duplicated per
 * location and the copies do not all carry the same number, so filtering here
 * drops phone numbers that have live threads at the very location being viewed
 * (measured on dev: scoping lost a real thread). The message search is already
 * location-scoped, so an out-of-location number simply matches nothing.
 *
 * NOTE: the index field is `locationId`, singular. `locationIds` parses fine and
 * silently matches zero rows.
 */
export function buildContactSearch(query: string): string | undefined {
  const trimmed = query.trim()
  if (!trimmed) return undefined
  const clauses: string[] = []
  const nameTerms = trimmed.split(/\s+/).map((term) => `*${escapeLucene(term)}*`)
  if (nameTerms.length) clauses.push(`name:(${nameTerms.join(` AND `)})`)
  const digits = phoneDigits(trimmed)
  if (digits.length >= MIN_PHONE_DIGITS) clauses.push(`cellPhone:*${digits}*`)
  if (!clauses.length) return undefined
  return `(${clauses.join(` OR `)}) AND _exists_:cellPhone`
}

/**
 * What a resolved inbox query matches. `phones` are the numbers of contacts
 * whose name or number matched; `digits` is the typed number itself, kept so a
 * thread with no contact record behind it is still findable.
 */
export type InboxSearchTargets = {
  phones: string[]
  digits: string
}

/**
 * Message-index clauses for a resolved query, or undefined when the query
 * matches nothing at all — the caller must then show an empty list rather than
 * falling back to an unfiltered one.
 */
export function buildSearchTargetClause(targets: InboxSearchTargets): string | undefined {
  const clauses = targets.phones.flatMap((phone) => [`from:"${phone}"`, `to:"${phone}"`])
  if (targets.digits.length >= MIN_PHONE_DIGITS) {
    clauses.push(`from:*${targets.digits}*`, `to:*${targets.digits}*`)
  }
  if (!clauses.length) return undefined
  return `(${clauses.join(` OR `)})`
}
